import React, { useState, useEffect } from "react";
import axios from "axios";
import { SETTINGS_CRUD_API } from "../../../hooks/APIHooks";

const Settings = () => {
  const [settings, setSettings] = useState({
    companylogo: "",
    companyName: "",
    companyPhone: "",
    companyPhone2: "",
    companyEmail: "",
    companyAddress: "",
    GSTnumber: "",
    Tax: "",
    invoice_number: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = () => {
    setIsLoading(true);
    axios
      .get(SETTINGS_CRUD_API)
      .then((response) => {
        if (response.data.error) {
          setMessage({ type: 'error', text: response.data.error });
          setSettings({
            companylogo: "",
            companyName: "",
            companyPhone: "",
            companyPhone2: "",
            companyEmail: "",
            companyAddress: "",
            GSTnumber: "",
            Tax: "",
            invoice_number: "",
          });
        } else {
          setSettings(response.data);
          setLogoError(false);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching settings:", error);
        setMessage({ type: 'error', text: 'Failed to load settings' });
        setIsLoading(false);
      });
  };

const getLogoUrl = (logoPath) => {
  if (!logoPath) {
    setLogoError(true);
    return '';
  }
  
  if (logoPath.startsWith('http')) return logoPath;
  
  const cleanPath = logoPath.replace(/^(\.?\/?)?/, '');
  
  // Handle different environments
  let baseUrl;
  const { hostname, port, protocol } = window.location;
  
  if (hostname === 'localhost' && port === '3000') {
    // React dev server on port 3000, APIs on port 80
    baseUrl = `${protocol}//${hostname}`;
  } else if (hostname === 'localhost' && !port) {
    // React build served from same domain as APIs
    baseUrl = window.location.origin;
  } else {
    // Production environment
    baseUrl = window.location.origin;
  }
  
  const fullUrl = `${baseUrl}/apis/${cleanPath}`;
  console.log('Logo URL:', fullUrl);
  return fullUrl;
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Invalid file type. Please select JPG, PNG, GIF, or WebP image.' });
        e.target.value = '';
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size too large. Maximum 5MB allowed.' });
        e.target.value = '';
        return;
      }
      
      setLogoFile(file);
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append("companyName", settings.companyName);
    formData.append("companyPhone", settings.companyPhone);
    formData.append("companyPhone2", settings.companyPhone2);
    formData.append("companyEmail", settings.companyEmail);
    formData.append("companyAddress", settings.companyAddress);
    formData.append("GSTnumber", settings.GSTnumber);
    formData.append("Tax", settings.Tax);
    formData.append("invoice_number", settings.invoice_number);
    
    if (logoFile) {
      formData.append("companylogo", logoFile);
    }

    axios
      .post(SETTINGS_CRUD_API, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.success) {
          setMessage({ type: 'success', text: response.data.success });
          setIsEditing(false);
          setLogoFile(null);
          setLogoError(false);
          fetchSettings();
        } else if (response.data.error) {
          setMessage({ type: 'error', text: response.data.error });
        }
      })
      .catch((error) => {
        console.error("Error updating settings:", error);
        setMessage({ type: 'error', text: 'Failed to update settings. Please try again.' });
      });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setMessage({ type: '', text: '' });
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setLogoFile(null);
    setMessage({ type: '', text: '' });
    fetchSettings();
  };

  const handleLogoError = () => {
    setLogoError(true);
    console.error('Failed to load logo:', getLogoUrl(settings.companylogo));
  };

  const handleLogoLoad = () => {
    setLogoError(false);
  };

  if (isLoading) {
    return (
      <div className="container p-8 max-w-4xl mx-auto mb-5">
        <div className="text-center">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="container p-8 max-w-4xl mx-auto mb-5">
      <h1 className="text-2xl font-bold text-center">Settings</h1>
      <p className="text-[12px] mb-4 text-center">
        Please make sure to add all your company details, this details will be
        effected throughout your system.
      </p>

      {message.text && (
        <div className={`mb-4 p-3 rounded-md ${
          message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          {message.text}
        </div>
      )}

      {isEditing ? (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Company Name *
              </label>
              <input
                type="text"
                name="companyName"
                value={settings.companyName}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Company Phone *
              </label>
              <input
                type="text"
                name="companyPhone"
                value={settings.companyPhone}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Company Phone 2
              </label>
              <input
                type="text"
                name="companyPhone2"
                value={settings.companyPhone2}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Company Email *
              </label>
              <input
                type="email"
                name="companyEmail"
                value={settings.companyEmail}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Address *
              </label>
              <textarea
                name="companyAddress"
                value={settings.companyAddress}
                onChange={handleChange}
                required
                rows="3"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                GST Number
              </label>
              <input
                type="text"
                name="GSTnumber"
                value={settings.GSTnumber}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Tax (%)
              </label>
              <input
                type="number"
                name="Tax"
                value={settings.Tax}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Invoice Number
              </label>
              <input
                type="text"
                name="invoice_number"
                value={settings.invoice_number}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="mb-4 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Logo
              </label>
              
              {settings.companylogo && !logoFile && (
                <div className="mb-2">
                  <p className="text-sm text-gray-600">Current Logo:</p>
                  <img
                    src={getLogoUrl(settings.companylogo)}
                    alt="Current Company Logo"
                    className="w-20 h-20 object-contain border rounded mt-1"
                    onError={handleLogoError}
                    onLoad={handleLogoLoad}
                  />
                </div>
              )}
              
              {logoFile && (
                <div className="mb-2">
                  <p className="text-sm text-gray-600">New Logo Preview:</p>
                  <img
                    src={URL.createObjectURL(logoFile)}
                    alt="New Company Logo Preview"
                    className="w-20 h-20 object-contain border rounded mt-1"
                  />
                </div>
              )}
              
              <input
                type="file"
                name="companylogo"
                onChange={handleLogoChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition duration-200"
            >
              Save Settings
            </button>
            <button
              type="button"
              onClick={handleCancelClick}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md transition duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6">
          {settings.companylogo && !logoError ? (
            <div className="mb-6 text-center">
              <img
                src={getLogoUrl(settings.companylogo)}
                alt="Company Logo"
                className="w-32 h-32 mx-auto object-contain border rounded-lg"
                onError={handleLogoError}
                onLoad={handleLogoLoad}
              />
            </div>
          ) : (
            <div className="mb-6 text-center">
              <div className="w-32 h-32 mx-auto border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <span className="text-gray-500 text-sm">No Logo</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Company Name</h2>
              <p className="font-normal text-gray-900">{settings.companyName || 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Company Phone</h2>
              <p className="font-normal text-gray-900">{settings.companyPhone || 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Company Phone 2</h2>
              <p className="font-normal text-gray-900">{settings.companyPhone2 || 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Company Email</h2>
              <p className="font-normal text-gray-900">{settings.companyEmail || 'Not set'}</p>
            </div>
            <div className="mb-4 md:col-span-2">
              <h2 className="font-semibold text-gray-700">Company Address</h2>
              <p className="font-normal text-gray-900">{settings.companyAddress || 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">GST Number</h2>
              <p className="font-normal text-gray-900">{settings.GSTnumber || 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Tax</h2>
              <p className="font-normal text-gray-900">{settings.Tax ? `${settings.Tax}%` : 'Not set'}</p>
            </div>
            <div className="mb-4">
              <h2 className="font-semibold text-gray-700">Invoice Number</h2>
              <p className="font-normal text-gray-900">{settings.invoice_number || 'Not set'}</p>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleEditClick}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition duration-200"
            >
              Edit Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;